/**
 * Summary Location field display (#999 QA Bugs 1–2).
 * Maps stored `locationTagId` to tag name for Summary rows — never show raw ids.
 */

export type SummaryLocationTagNameResolver = (
  locationTagId: number | null | undefined,
) => string | null;

/**
 * Resolve a Summary Location row value from a unit's `locationTagId`.
 * Untagged / missing / invalid → `null` (Summary renders `—`).
 */
export function formatSummaryLocationFieldValue(
  locationTagId: unknown,
  resolveName: SummaryLocationTagNameResolver,
): string | null {
  if (locationTagId == null || locationTagId === '') {
    return null;
  }
  if (typeof locationTagId === 'number') {
    if (!Number.isFinite(locationTagId)) {
      return null;
    }
    return resolveName(locationTagId);
  }
  if (typeof locationTagId === 'string') {
    const trimmed = locationTagId.trim();
    if (trimmed === '') {
      return null;
    }
    const asNumber = Number(trimmed);
    if (!Number.isFinite(asNumber) || String(asNumber) !== trimmed) {
      return null;
    }
    return resolveName(asNumber);
  }
  return null;
}

/**
 * Apply Location name resolution when the Summary row is `locationTagId`;
 * otherwise pass the raw display value through unchanged.
 */
export function withSummaryLocationFieldDisplay(
  dataField: string,
  rawValue: string | number | boolean | null | undefined,
  resolveName: SummaryLocationTagNameResolver,
): string | number | boolean | null | undefined {
  if (dataField !== 'locationTagId') {
    return rawValue;
  }
  return formatSummaryLocationFieldValue(rawValue, resolveName);
}
